/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { Box, Grow, Popper, Skeleton, type TextFieldVariants } from '@mui/material';
import { prettifySnakeCase, type ValueFormat } from '@poweralpha/common';
import type { StandardSchemaV1Issue } from '@tanstack/react-form';
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import InputKeyValue from '../display';
import { FormatValue, type FormatValueProps } from '../FormatValue';
import { BaseTextField, type BaseTextFieldProps } from './BaseTextField';
import { useFieldContext } from './formContext';

const getDisplayProps = (
  props: BaseTextFieldProps,
  formatValueProps: Props['displayFormatProps'],
) => {
  let format: ValueFormat | undefined;
  if (typeof props.numberFormat === 'object') {
    format = props.numberFormat.dollar ? 'dollar' : undefined;
  } else if (typeof props.numberFormat === 'string') {
    format = props.numberFormat;
  } else if (props.rightUnit === '%') {
    format = 'percent';
  }

  return {
    format,
    precision:
      (typeof props.numberFormat === 'object' ? props.numberFormat.decimalScale : undefined) ?? 1,
    postfixUnits:
      props.rightUnit && props.rightUnit !== '%' && props.rightUnit[0] !== ' '
        ? ` ${props.rightUnit}`
        : props.rightUnit,
    ...formatValueProps,
  };
};

type Props<Variant extends TextFieldVariants = TextFieldVariants> = BaseTextFieldProps<Variant> & {
  label?: React.ReactNode;
  externalErrors?: Partial<Record<string, Array<StandardSchemaV1Issue>>>;
  hideErrorText?: boolean;
  enableErrorPopper?: boolean;
  displayOnly?: boolean;
  displayFormatProps?: Omit<FormatValueProps<number | string>, 'value'>;
  isLoading?: boolean;
};

function FormTextField<Variant extends TextFieldVariants = TextFieldVariants>({
  label,
  externalErrors,
  hideErrorText,
  enableErrorPopper,
  displayOnly,
  displayFormatProps,
  isLoading,
  ...props
}: Readonly<Props<Variant>>) {
  const { onFocus, onBlur } = props;
  const field = useFieldContext<string>();
  const anchorRef = useRef<HTMLDivElement>(null);
  const labelRender = label ?? prettifySnakeCase(field.name.split('.').at(-1));

  const [errorPopperOpen, setErrorPopperOpen] = useState(false);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => field.handleChange(e.target.value),
    [field],
  );

  const error = useMemo(
    () => field.state.meta.errors[0]?.message ?? externalErrors?.[field.name]?.[0]?.message,
    [field.state.meta.errors, externalErrors, field.name],
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      if (error) {
        setErrorPopperOpen(true);
      }
      onFocus?.(e);
    },
    [error, onFocus],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setErrorPopperOpen(false);
      onBlur?.(e);
    },
    [onBlur],
  );

  useEffect(() => {
    if (!enableErrorPopper) return;
    setErrorPopperOpen(Boolean(error));
    return () => setErrorPopperOpen(false);
  }, [enableErrorPopper, error]);

  if (isLoading) {
    return <Skeleton />;
  }

  if (displayOnly) {
    const value = props.numberFormat ? Number(field.state.value) : field.state.value;
    const { postfixUnits, ...formatProps } = getDisplayProps(props, displayFormatProps);
    return (
      <InputKeyValue
        value={<FormatValue {...formatProps} value={value as number} />}
        label={labelRender}
        postfixUnits={postfixUnits}
        sx={{ pt: 0, pb: 0 }}
      />
    );
  }

  return (
    <>
      <BaseTextField
        {...props}
        ref={anchorRef}
        variant={props.variant}
        size={props.size ?? 'small'}
        fullWidth
        name={field.name}
        label={labelRender}
        value={field.state.value}
        error={Boolean(error)}
        helperText={hideErrorText ? '' : error}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      {enableErrorPopper && (
        <Popper
          open={errorPopperOpen}
          anchorEl={anchorRef.current}
          transition
          disablePortal
          sx={{ zIndex: 1300 }}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
              }}
            >
              <Box
                sx={{
                  minWidth: 80,
                  maxWidth: 200,
                  mt: 1,
                  p: 0.5,
                  pr: 1,
                  pl: 1,
                  borderRadius: 2,
                  bgcolor: 'background.default',
                  fontSize: 12,
                  textAlign: 'center',
                  color: 'error.main',
                }}
              >
                {error}
              </Box>
            </Grow>
          )}
        </Popper>
      )}
    </>
  );
}

export default FormTextField;
